import React, { useState } from "react";
import { Star, X, Loader2, ImagePlus } from "lucide-react";
import { api } from "@/lib/api";
import { resolveImgUrl } from "@/components/ImageUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const StarRating = ({ value = 0, size = 16, className = "" }) => {
  const v = Math.round(value);
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          style={{ width: size, height: size }}
          className={i <= v ? "text-amber-400 fill-amber-400" : "text-slate-300"}
        />
      ))}
    </span>
  );
};

export const StarSelector = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="star-selector">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}
          data-testid={`star-pick-${i}`}
          className="p-0.5 transition-transform hover:scale-110"
        >
          <Star className={`w-8 h-8 ${i <= (hover || value) ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
};

export function ReviewModal({ open, onClose, product, orderId, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  if (!open) return null;

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/reviews/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, r.data.url]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async () => {
    if (rating < 1) { toast.error("Please pick a star rating"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", {
        product_id: product.product_id, order_id: orderId,
        rating, title, body, photos,
      });
      toast.success("Thanks! Your review is submitted for approval.");
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40" data-testid="review-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg p-6 sm:p-7 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-xl font-semibold">Write a review</h3>
            <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{product?.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="review-modal-close"><X className="w-5 h-5"/></button>
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Your rating</div>
            <StarSelector value={rating} onChange={setRating} />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Title</div>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Loved it!" data-testid="review-title" maxLength={120}/>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Your review</div>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Share what you liked about the fit, fabric and quality…" data-testid="review-body" maxLength={2000}/>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Photos (optional)</div>
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden bg-slate-100">
                  <img src={resolveImgUrl(p)} alt="" className="w-full h-full object-cover"/>
                  <button type="button" onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))}
                    className="absolute top-0 right-0 bg-black/60 text-white rounded-bl-md p-0.5"><X className="w-3 h-3"/></button>
                </div>
              ))}
              <label className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-pink-400 text-slate-400" data-testid="review-photo-upload">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin"/> : <ImagePlus className="w-5 h-5"/>}
                <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={uploading}/>
              </label>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="review-submit"
            className="rounded-full brand-gradient-pink text-white border-0">{busy ? "Submitting…" : "Submit review"}</Button>
        </div>
      </div>
    </div>
  );
}
